/**
 * What the extraction actually did, per model.
 *
 * FieldsPanel above deliberately shows one reconciled answer per field — that is the
 * reviewer's job, and burying it in raw model output would defeat it. This is the other
 * audience: when a field is blank or contested and the question is *why*, the answer is
 * which model said what, which one errored, and what it thought it was looking at. That
 * is all already on the wire (`run.models[]`), it just had nowhere to be shown.
 *
 * Collapsed by default and last in the panel, because it is a debugging affordance, not
 * part of the review flow.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, Button } from '../../ui/core';
import { hairline, softFill } from '../../ui/styles';
import { ChevronDownIcon } from '../../ui/icons';
import { SectionLabel, timestamp } from './components';

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '11.5px' };

/**
 * Bring a disclosure's own header to the top of the scroll container when it opens.
 *
 * This panel is the LAST thing in the side panel, so expanding it pushed everything it
 * revealed below the fold — you opened it and nothing appeared to happen. Aligning to the
 * header rather than the bottom of the content keeps the thing you just clicked visible
 * and gives the body the whole remaining height, which matters because a model's field
 * list can be taller than the container.
 */
const useRevealOnOpen = (open) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    ref.current?.scrollIntoView({
      block: 'start',
      // Honours the OS setting rather than animating unconditionally.
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [open]);

  return ref;
};

/** A disclosure whose whole header is a real button — keyboard-operable, announces state. */
const Row = ({ id, summary, tone, meta, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRevealOnOpen(open);

  return (
    <Box ref={ref} sx={{ borderTop: (t) => `1px solid ${hairline(t)}` }}>
      <Stack
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={id}
        direction="row"
        alignItems="baseline"
        spacing={1.25}
        sx={{
          width: '100%',
          px: 1.25,
          py: 0.9,
          border: 0,
          background: 'none',
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': { backgroundColor: (t) => softFill(t) },
        }}
      >
        <Typography sx={{ ...mono, fontWeight: 600, color: tone, minWidth: 0, flex: 1, wordBreak: 'break-all' }}>
          {summary}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', whiteSpace: 'nowrap' }}>{meta}</Typography>
        <ChevronDownIcon
          sx={{
            fontSize: 15,
            display: 'block',
            color: 'text.disabled',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Stack>
      {open && (
        <Box id={id} sx={{ backgroundColor: (t) => softFill(t), px: 1.25, py: 1 }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const KV = ({ label, value }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.15 }}>
    <Typography sx={{ ...mono, color: 'text.secondary', minWidth: 130, flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{ ...mono, wordBreak: 'break-word' }}>{value === '' || value == null ? '—' : String(value)}</Typography>
  </Stack>
);

const AGREEMENT_TONE = {
  unanimous: 'success.main',
  majority: 'warning.main',
  split: 'error.main',
};

const DebugPanel = ({ run }) => {
  const [open, setOpen] = useState(false);
  const ref = useRevealOnOpen(open);

  // Nothing to debug before a run exists. Rendered as nothing rather than as an empty
  // section, so the panel doesn't grow a dead heading on documents that never ran.
  if (!run) return null;

  const models = run.models || [];
  // modelsRun is the ASKED list and models[] is what came back. A model in the first and
  // not the second never reported at all, which is the failure this section exists to
  // make visible — an absent row is otherwise indistinguishable from a model not in the
  // rollout.
  const silent = (run.modelsRun || []).filter((m) => !models.some((r) => r.model === m));

  return (
    <Box ref={ref}>
      <Stack
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="extraction-debug"
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{
          border: 0,
          background: 'none',
          p: 0,
          font: 'inherit',
          cursor: 'pointer',
          color: 'text.secondary',
        }}
      >
        <SectionLabel>Model extraction debug</SectionLabel>
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
          {models.length} model{models.length === 1 ? '' : 's'}
        </Typography>
        {/* Coloured on the COLLAPSED header, so a split run is visible without opening
            anything — that is the one state where the reconciled fields above are
            deliberately empty and the panel is worth expanding. */}
        {run.agreement && (
          <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: AGREEMENT_TONE[run.agreement] }}>
            {run.agreement}
          </Typography>
        )}
        <ChevronDownIcon sx={{ fontSize: 15, display: 'block', transform: open ? 'rotate(180deg)' : 'none' }} />
      </Stack>

      {open && (
        <Box id="extraction-debug" sx={{ mt: 1, border: (t) => `1px solid ${hairline(t)}`, borderRadius: 1 }}>
          <Box sx={{ px: 1.25, py: 1 }}>
            <KV label="run" value={run.id} />
            <KV label="status" value={run.status} />
            <KV label="agreement" value={run.agreement} />
            <KV label="confidence" value={run.confidence} />
            <KV label="detected type" value={run.detectedDocumentType} />
            <KV label="validation" value={(run.validationIssues || []).join(', ')} />
            <KV label="asked" value={(run.modelsRun || []).join(', ')} />
            {silent.length > 0 && <KV label="never reported" value={silent.join(', ')} />}
            <KV label="created" value={timestamp(run.createdAt)} />
            <KV label="finished" value={run.finishedAt ? timestamp(run.finishedAt) : null} />
          </Box>

          {run.disagreements?.length > 0 && (
            <Row
              id="extraction-debug-disagreements"
              summary="disagreements"
              tone="error.main"
              meta={`${run.disagreements.length} field${run.disagreements.length === 1 ? '' : 's'}`}
            >
              {run.disagreements.map((d) => (
                <KV key={d.field} label={d.field} value={JSON.stringify(d.values)} />
              ))}
            </Row>
          )}

          {models.map((m) => (
            <Row
              key={m.model}
              id={`extraction-debug-${m.model}`}
              summary={m.model}
              tone={m.ok ? 'text.primary' : 'error.main'}
              meta={
                m.ok
                  ? [
                      m.confidence == null ? null : `conf ${m.confidence}`,
                      m.detectedDocumentType,
                      `${m.entries?.length || 0} entr${m.entries?.length === 1 ? 'y' : 'ies'}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : 'failed'
              }
            >
              {/* The error is the whole story when a model failed — showing an empty
                  field list underneath it would read as "returned nothing" rather than
                  "never answered". */}
              {m.error ? (
                <Typography sx={{ ...mono, color: 'error.main', wordBreak: 'break-word' }}>{m.error}</Typography>
              ) : (
                <>
                  {m.validationIssues?.length > 0 && (
                    <KV label="validation" value={m.validationIssues.join(', ')} />
                  )}
                  {(m.entries || []).length === 0 && (
                    <Typography sx={{ ...mono, color: 'text.secondary' }}>
                      Reported no entries — the model answered but found nothing extractable.
                    </Typography>
                  )}
                  {(m.entries || []).map((entry, i) => (
                    <Box key={entry.key || i} sx={{ mt: i === 0 ? 0 : 1 }}>
                      {/* Insurance returns one entry per insured vehicle, so the entry key
                          is what tells you WHICH car a value belongs to. */}
                      {entry.key && (
                        <Typography sx={{ ...mono, color: 'text.secondary', mb: 0.25 }}>{entry.key}</Typography>
                      )}
                      {(entry.fields || []).map((f) => (
                        <KV key={f.key} label={f.key} value={f.value} />
                      ))}
                    </Box>
                  ))}
                </>
              )}
            </Row>
          ))}

          <Box sx={{ borderTop: (t) => `1px solid ${hairline(t)}`, px: 1.25, py: 0.75 }}>
            {/* For pasting into a ticket. The rendered view is for reading; a bug report
                needs the payload verbatim, including whatever this panel doesn't show. */}
            <Button
              size="small"
              variant="text"
              onClick={() => navigator.clipboard?.writeText(JSON.stringify(run, null, 2))}
              sx={{ textTransform: 'none', fontSize: '11.5px' }}
            >
              Copy raw JSON
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DebugPanel;
